import { useTheme, Image, Spinner } from "@wayflyer/flyui";
import {
  IconWarningCircle24Line as ExclamationCircleIcon,
  IconCheckCircle24Line as CheckCircleIcon,
} from "@wayflyer/flyui-icons/24/line";
import { IconBank32Line as DefaultBankIcon } from "@wayflyer/flyui-icons/32/line";
import type { ReactNode } from "react";
import { styled } from "styled-components";

export type BankReconnectFunction = (bankConnection: BankConnection) => void;

export interface BankAccount {
  id: string;
  name: string;
  type: string;
  pull_enabled: boolean;
  mask: string;
}
export interface BankConnection {
  institutionID: string;
  institutionName: string;
  id: string;
  accessible: boolean;
  accounts?: BankAccount[];
  accountsSelected?: boolean;
  providerName?: string;
}

export type LogoLoadingState = {
  loading: boolean;
  couldLoad?: boolean;
  data?: string;
  type?: "url" | "base64";
};

export interface BankConnectionDisplayProps {
  bankConnection: BankConnection;
  logoState?: LogoLoadingState;
  startReconnect: BankReconnectFunction;
}

const ReconnectBankButton = styled.button`
  all: unset;
  cursor: pointer;
`;

const BankIconDisplay = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: var(--sizes-spacing-3);
`;

const BankConnectionDisplayContainer = styled.div`
  box-sizing: content-box;
  width: 84px;
  display: flex;
  flex-direction: column;
  align-content: center;
  margin-right: var(--sizes-spacing-6);
  position: relative;
`;

const BankConnectBadge = styled.span`
  border-radius: var(--sizes-radius-xl);
  background-color: var(--palette-content-neutral-subtle);
  right: -7px;
  top: -7px;
  position: absolute;
  text-align: center;
  line-height: 19px;
  z-index: 100;
  width: 22px;
  height: 22px;
`;

const BankConnectionNotAccessibleBadgeIcon = styled(ExclamationCircleIcon)`
  font-size: 22px;
  color: var(--palette-content-status-critical);
  background-color: white;
`;

const BankConnectionAccessibleBadgeIcon = styled(CheckCircleIcon)`
  font-size: 22px;
  color: var(--palette-content-status-positive);
  background-color: white;
`;

const BankLogoContainer = styled.div`
  width: 84px;
  height: 84px;
  border: solid 2px ${({ color }) => color};
  border-radius: var(--sizes-radius-sm);
  position: relative;
  max-height: 84px;
  max-width: 84px;
`;

const BankIcon = styled(Image)`
  width: 100%;
  height: 100%;
`;

const BankConnectionDetailContainer = styled.p`
  text-align: center;
  color: var(--palette-content-neutral-strong);
`;

const BankConnectionDetailSpan = styled.span`
  font-weight: var(--typography-fontWeight-medium);
`;

type BankConnectionStatusBadgeProps = {
  bankConnection: BankConnection;
};

const BankConnectionStatusBadge = ({ bankConnection }: BankConnectionStatusBadgeProps) => {
  if (!bankConnection.accessible) {
    return (
      <BankConnectBadge>
        <BankConnectionNotAccessibleBadgeIcon />
      </BankConnectBadge>
    );
  }
  return (
    <BankConnectBadge>
      <BankConnectionAccessibleBadgeIcon />
    </BankConnectBadge>
  );
};

interface BankConnectionDisplayWrapperProps extends BankConnectionDisplayProps {
  children: ReactNode;
}

const ReconnectBankWrapper = ({ bankConnection, startReconnect, children }: BankConnectionDisplayWrapperProps) => {
  if (!bankConnection.accessible) {
    return (
      <ReconnectBankButton
        aria-label="Reconnect Bank"
        onClick={() => {
          startReconnect(bankConnection);
        }}
      >
        {children}
      </ReconnectBankButton>
    );
  } else {
    return <>{children}</>;
  }
};

export type BankLogoDisplayProps = {
  logoState?: LogoLoadingState;
};

const BankLogoDisplay = ({ logoState }: BankLogoDisplayProps) => {
  if (!logoState || logoState.loading) {
    return <Spinner size={16} />;
  } else if (!logoState.loading && logoState.couldLoad && logoState.data) {
    const srcPrefix = logoState.type === "url" ? "" : "data:image/png;base64,";
    return <BankIcon src={`${srcPrefix}${logoState.data}`} alt="Bank Logo" />;
  } else {
    return <DefaultBankIcon title="Bank logo" />;
  }
};

/**
 * BankConnectionDisplay component
 */
export const BankConnectionDisplay = ({ bankConnection, logoState, startReconnect }: BankConnectionDisplayProps) => {
  const theme = useTheme();
  return (
    <ReconnectBankWrapper bankConnection={bankConnection} startReconnect={startReconnect}>
      <BankConnectionDisplayContainer>
        <BankConnectionStatusBadge bankConnection={bankConnection} />
        <BankLogoContainer
          color={
            bankConnection.accessible ? theme.palette.content.status.positive : theme.palette.border.status.critical
          }
        >
          <BankIconDisplay>
            <BankLogoDisplay logoState={logoState} />
          </BankIconDisplay>
        </BankLogoContainer>
        <BankConnectionDetailContainer>
          <BankConnectionDetailSpan>····</BankConnectionDetailSpan>
        </BankConnectionDetailContainer>
      </BankConnectionDisplayContainer>
    </ReconnectBankWrapper>
  );
};
